import React, { useEffect, useState, useRef, useCallback } from "react";
import { FaFileMedicalAlt, FaTimes, FaSave, FaRecycle } from "react-icons/fa";
import Cookies from "js-cookie";
import Logo from "../../images/logopdf.png";
import WatermarkLogo from "../../assets/afrafep.png";
import api from "../../config/configApi";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { jsPDF } from "jspdf";

interface Professional {
  idProfissional: number;
  nmProfissional: string;
  nomePaciente: string;
  cpf: string;
  crm: string;
  especialidade: string;
}

interface ReceituarioLaudo {
  id: number;
  tipo: string;
  dtConsulta: string;
  hrConsulta: string;
  cid: string;
  atestadoQtdDias: number;
  prescricao: string;
  criadoEm: string;
  editadoEm: string;
  profissional: {
    idProfissional: number;
    nmProfissional: string;
    CRM?: string;
    especialidade: {
      idEspecialidade?: number;
      dsEspecialidade: string;
      exibeQuestEpidem?: string;
      realizaProced?: string;
    };
  };
  paciente: {
    cdPaciente: number;
    nrCarteiraPlano: string;
    nmPaciente: string;
    dtNascimento: string;
    flSexo: string;
    cdStatus: number;
    nuCpf: string;
    nmEndereco: string;
    nuEndereco: number;
    nmComplEndereco: string;
    nmBairroEndereco: string;
    nmCidadeEndereco: string;
    nmUfEndereco: string;
    cdCepEndereco: string;
    cartaoSus: string;
  };
}

interface AtestadoProps {
  onClose?: () => void;
}

const AtestadoMedico: React.FC<AtestadoProps> = ({ onClose }) => {
  const [nomePaciente, setNomePaciente] = useState("");
  const [cpf, setCpf] = useState("");
  const [horarioConsulta, setHorarioConsulta] = useState("");
  const [cid, setCid] = useState("");
  const [diasAfastamento, setDiasAfastamento] = useState("");
  const [diasEscritoAfastamento, setDiasEscritoAfastamento] = useState("");
  const [dataAtestado, setDataAtestado] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [atestadosExistentes, setAtestadosExistentes] = useState<ReceituarioLaudo[]>([]);
  const [showAtestadosList, setShowAtestadosList] = useState(false);
  const [loadingAtestados, setLoadingAtestados] = useState(false);

  const professional: Professional = {
    idProfissional: Number(Cookies.get("idProfissional")),
    nmProfissional: Cookies.get("nmProfissional") || "Médico não identificado",
    nomePaciente: Cookies.get("Nome") || "Paciente não identificado",
    cpf: Cookies.get("NuCPF") || "CPF não identificado",
    crm: Cookies.get("CRM") || "CRM não informado",
    especialidade:
      Cookies.get("dsEspecialidade") || "Especialidade não informada",
  };

  // Função para converter número em dias por extenso
  const diasPorExtenso = (dias: number) => {
    if (dias === 0) return "zero";
    const unidades = [
      "", "um", "dois", "três", "quatro", "cinco", "seis", "sete", "oito", "nove"
    ];
    const dezenas = [
      "", "dez", "vinte", "trinta", "quarenta", "cinquenta", 
      "sessenta", "setenta", "oitenta", "noventa"
    ];
    const especiais = [
      "dez", "onze", "doze", "treze", "quatorze", "quinze", 
      "dezesseis", "dezessete", "dezoito", "dezenove"
    ];

    if (dias < 10) return unidades[dias];
    if (dias < 20) return especiais[dias - 10];
    if (dias < 100) {
      const dezena = Math.floor(dias / 10);
      const unidade = dias % 10;
      return dezenas[dezena] + (unidade > 0 ? " e " + unidades[unidade] : "");
    }
    return dias.toString();
  };

  // Função para buscar atestados existentes do paciente
  const fetchAtestadosExistentes = async () => {
    setLoadingAtestados(true);
    try {
      const token = sessionStorage.getItem("token");
      const cpfPaciente = professional.cpf;

      if (token && cpfPaciente) {
        const response = await api.get<ReceituarioLaudo[]>(
          `/sisclinic/receituarios/cpf/${cpfPaciente}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        // Filtrar apenas atestados (tipo "ATESTADO") e ordenar por data mais recente
        const atestados = response.data
          .filter(item => item.tipo === "ATESTADO")
          .sort((a, b) => {
            const dateA = new Date(`${a.dtConsulta}T${a.hrConsulta}`);
            const dateB = new Date(`${b.dtConsulta}T${b.hrConsulta}`);
            return dateB.getTime() - dateA.getTime();
          });

        setAtestadosExistentes(atestados);
      }
    } catch (error) {
      console.error("Erro ao buscar atestados existentes:", error);
      toast.error("Erro ao carregar atestados anteriores");
    } finally {
      setLoadingAtestados(false);
    }
  };

  // Função para carregar um atestado existente no formulário
  const carregarAtestadoExistente = (atestado: ReceituarioLaudo) => {
    setNomePaciente(atestado.paciente.nmPaciente);
    setCpf(formatCPF(atestado.paciente.nuCpf || ""));
    setHorarioConsulta(atestado.hrConsulta || "");
    setCid(atestado.cid || "");
    
    const dias = atestado.atestadoQtdDias || 0;
    setDiasAfastamento(dias.toString());
    
    // Converter automaticamente os dias para por extenso
    setDiasEscritoAfastamento(diasPorExtenso(dias));
    
    setShowAtestadosList(false);
    toast.success("Atestado carregado com sucesso!");
  };

  // Função para converter automaticamente quando o usuário digitar
  const handleDiasAfastamentoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, ""); // Remove não-números
    setDiasAfastamento(value);
    
    // Converte automaticamente para por extenso
    if (value) {
      const diasNumero = parseInt(value, 10);
      setDiasEscritoAfastamento(diasPorExtenso(diasNumero));
    } else {
      setDiasEscritoAfastamento("");
    }
  };

  const formatCPF = (cpf: string) => {
    return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  };

  const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 11) {
      setCpf(formatCPF(value));
    }
  };

  const handleGenerateAtestado = () => {
    setIsModalOpen(true);
    setNomePaciente(professional.nomePaciente);
    setCpf(formatCPF((professional.cpf || "").replace(/\D/g, "")));

    const today = new Date();
    setDataAtestado(today.toLocaleDateString("pt-BR"));

    const hours = String(today.getHours()).padStart(2, "0");
    const minutes = String(today.getMinutes()).padStart(2, "0");
    setHorarioConsulta(`${hours}:${minutes}`);
    
    // Buscar atestados existentes quando abrir o modal
    fetchAtestadosExistentes();
  };

  const handleHorarioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const cleanedValue = value.replace(/[^0-9:]/g, "");

    if (/^([0-1]?[0-9]|2[0-3]):?([0-5][0-9])?$/.test(cleanedValue)) {
      if (
        cleanedValue.length === 2 &&
        value.length === 2 &&
        !value.includes(":")
      ) {
        setHorarioConsulta(cleanedValue + ":");
      } else {
        setHorarioConsulta(cleanedValue);
      }
    }
  };

  const handleClose = useCallback(() => {
    setIsModalOpen(false);
    setNomePaciente("");
    setCpf("");
    setHorarioConsulta("");
    setCid("");
    setDiasAfastamento("");
    setDiasEscritoAfastamento("");
    setShowAtestadosList(false);
    onClose?.();
  }, [onClose]); // Include onClose as a dependency
  
  const handleGeneratePDF = async () => {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "pt",
      format: "a4",
    });

    // Configurações de layout
    const marginLeft = 40;
    const marginRight = 40;
    const marginTop = 0;
    const pageWidth =
      doc.internal.pageSize.getWidth() - marginLeft - marginRight;
    const centerX = doc.internal.pageSize.getWidth() / 2;

    // 1. Logo no topo (centralizada)
    const logoImg = new Image();
    logoImg.src = Logo;

    await new Promise((resolve) => {
      logoImg.onload = () => {
        const logoWidth = 600; // Largura fixa para a logo
        const logoHeight = (logoImg.height * logoWidth) / logoImg.width;
        doc.addImage(
          logoImg,
          "PNG",
          centerX - logoWidth / 2, // Centraliza horizontalmente
          marginTop,
          logoWidth,
          logoHeight
        );
        resolve(null);
      };
    });

    // 2. Cabeçalho textual
    const headerY = marginTop + 150;
    doc.setFontSize(12);
    doc.setTextColor(36, 99, 235); // Azul #2463EB
    doc.text(
      "Associação dos Auditores Fiscais do Estado da Paraíba",
      centerX,
      headerY,
      { align: "center" }
    );

    // 3. Título principal (azul e centralizado)
    doc.setFontSize(18);
    doc.setTextColor(36, 99, 235); // Azul #2463EB
    doc.setFont("helvetica", "bold");
    doc.text("ATESTADO MÉDICO", centerX, headerY + 30, { align: "center" });

    // 4. Conteúdo do atestado
    const contentY = headerY + 80;
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0); // Preto
    doc.setFont("helvetica", "normal");

    // Primeiro parágrafo
    let yPosition = contentY;
    const firstParagraph = `Atesto, para os devidos fins, a pedido do interessado, que o Sr.(a) ${nomePaciente}, portador do CPF: ${cpf} foi submetido à consulta nesta data, no horário das ${horarioConsulta}, sendo portador de afecção CID-${cid}.`;
    const splitFirstParagraph = doc.splitTextToSize(firstParagraph, pageWidth);
    doc.text(splitFirstParagraph, marginLeft, yPosition);
    yPosition += splitFirstParagraph.length * 20;

    // Segundo parágrafo
    const secondParagraph = `Em decorrência deverá permanecer afastado de suas atividades laborativas por um período de ${diasAfastamento} (${diasEscritoAfastamento}) dia(s) a partir desta data. João Pessoa, ${dataAtestado}.`;
    const splitSecondParagraph = doc.splitTextToSize(
      secondParagraph,
      pageWidth
    );
    doc.text(splitSecondParagraph, marginLeft, yPosition + 20);
    yPosition += splitSecondParagraph.length * 20 + 40;

    // 5. Assinatura
    doc.text("_________________________________________", centerX, yPosition, {
      align: "center",
    });
    doc.setFont("helvetica", "bold");
    doc.text(`${professional.nmProfissional}`, centerX, yPosition + 20, {
      align: "center",
    });
    doc.setFont("helvetica", "normal");
    doc.text(`CRM: ${professional.crm}`, centerX, yPosition + 40, {
      align: "center",
    });
    doc.text(professional.especialidade, centerX, yPosition + 60, {
      align: "center",
    });

    // 6. Seção de Autorização
    yPosition += 140;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("Autorização", centerX, yPosition, { align: "center" });
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");

    const authorizationText = `Eu, ${nomePaciente}, autorizo o Dr. ${professional.nmProfissional} a registrar o diagnóstico (codificado - CID: ${cid} ou por extenso) neste atestado.`;
    const splitAuthorization = doc.splitTextToSize(
      authorizationText,
      pageWidth
    );
    doc.text(splitAuthorization, marginLeft, yPosition + 30);

    yPosition += splitAuthorization.length * 20 + 60;
    doc.text("_________________________________________", centerX, yPosition, {
      align: "center",
    });
    doc.text("Assinatura do Usuário - RG", centerX, yPosition + 20, {
      align: "center",
    });

    // 5. Marca d'água (transparente)
    const watermarkImg = new Image();
    watermarkImg.src = WatermarkLogo;

    await new Promise((resolve) => {
      watermarkImg.onload = () => {
        const watermarkWidth = 300;
        const watermarkHeight =
          (watermarkImg.height * watermarkWidth) / watermarkImg.width;
        const centerX = doc.internal.pageSize.getWidth() / 2;
        const centerY = doc.internal.pageSize.getHeight() / 2;

        const canvas = document.createElement("canvas");
        canvas.width = watermarkImg.width;
        canvas.height = watermarkImg.height;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        // Desenha a imagem original
        ctx.drawImage(watermarkImg, 0, 0, canvas.width, canvas.height);

        // Pega todos os pixels
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;

        // Cor azul #2463EB
        const newR = 36;
        const newG = 99;
        const newB = 235;

        // Opacidade (0 a 1)
        const opacity = 0.2; // 10% visível

        // Loop pelos pixels
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];

          // Detecta pixels vermelhos
          if (r > 150 && g < 80 && b < 80) {
            data[i] = newR; // R
            data[i + 1] = newG; // G
            data[i + 2] = newB; // B
            data[i + 3] = Math.floor(opacity * 255); // Opacidade aplicada
          }
        }

        // Aplica alterações
        ctx.putImageData(imageData, 0, 0);

        // Adiciona ao PDF
        doc.addImage(
          canvas,
          "PNG",
          centerX - watermarkWidth / 2,
          centerY - watermarkHeight / 2,
          watermarkWidth,
          watermarkHeight
        );

        resolve(null);
      };
    });

    // 8. Rodapé em duas colunas
    const footerY = doc.internal.pageSize.getHeight() - 80;
    doc.setFontSize(8);
    doc.setTextColor(36, 99, 235); // Azul #2463EB

    // Coluna da esquerda (informações de contato)
    const leftColX = marginLeft;
    doc.text(
      "Rua Corálio Soares de Oliveira, 497 • Centro, João Pessoa • PB",
      leftColX,
      footerY
    );
    doc.text("Fone: (83) 3533.5310 | 3533.5311", leftColX, footerY + 15);
    doc.text("www.afrafep.org.br", leftColX, footerY + 30);
    doc.text("INSC. ANS N° 330281", leftColX, footerY + 45);

    // Coluna da direita (valores)
    const rightColX = doc.internal.pageSize.getWidth() - marginRight - 200;
    doc.text("Ética e Transparência", rightColX, footerY);
    doc.text(
      "Solidariedade e Responsabilidade Social",
      rightColX,
      footerY + 15
    );
    doc.text("Comprometimento e Profissionalismo", rightColX, footerY + 30);
    doc.text("Legalidade e Respeito", rightColX, footerY + 45);

    // Gerar PDF e abrir diálogo de impressão diretamente
  const pdfBlob = doc.output("blob");
  const pdfUrl = URL.createObjectURL(pdfBlob);
  
  // Criar iframe para impressão automática
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.src = pdfUrl;
  document.body.appendChild(iframe);
  
  iframe.onload = function() {
    setTimeout(function() {
      iframe.contentWindow?.print();
    }, 500);
  };
  
  return pdfBlob;
  };

  const handleSaveAtestado = async () => {
    if (!cpf || !cid || !diasAfastamento) {
      toast.error("Preencha todos os campos obrigatórios do atestado!", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      return;
    }

    setIsSubmitting(true);

    const currentDate = new Date();
    const formattedDate = currentDate.toISOString().split("T")[0];
    const formattedTime = currentDate.toTimeString().substring(0, 5);

    const payload = {
      nuCpf: cpf.replace(/\D/g, ""),
      idProfissional: professional.idProfissional,
      tipo: "ATESTADO",
      dtConsulta: formattedDate,
      hrConsulta: formattedTime,
      cid: cid,
      atestadoQtdDias: diasAfastamento,
      prescricao: `Atestado para ${nomePaciente} (CPF: ${cpf}). CID: ${cid}. Afastamento por ${diasAfastamento} dias (${diasEscritoAfastamento}).`,
    };

    try {
      const response = await api.post("/receituarios", payload);

      if (response.status === 200 || response.status === 201) {
        toast.success("Atestado salvo com sucesso!");
        handleGeneratePDF();
        setTimeout(() => {
          handleClose();
          setIsSubmitting(false);
        }, 3500);
      } else {
        throw new Error(`Erro ao salvar atestado: ${response.status}`);
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Ocorreu um erro ao salvar o atestado");
      setIsSubmitting(false);
    }
  };
  
 useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isModalOpen) handleClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isModalOpen, handleClose]); // Add handleClose to dependencies

  useEffect(() => {
    if (isModalOpen && modalRef.current) modalRef.current.focus();
  }, [isModalOpen]);

  // Função para formatar data CORRIGIDA - sem problemas de fuso horário
  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    
    // Divide a data no formato YYYY-MM-DD
    const [year, month, day] = dateString.split('-');
    
    // Cria a data no formato DD/MM/YYYY sem problemas de fuso horário
    return `${day}/${month}/${year}`;
  };

  return (
    <div className="space-y-4">
      <button
        onClick={handleGenerateAtestado}
        className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded flex items-center gap-2"
      >
        <FaFileMedicalAlt />
        Atestado Médico
      </button>

      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          role="dialog"
          aria-modal="true"
          onClick={handleClose}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col print:p-8 print:max-w-[800px]"
            ref={modalRef}
            tabIndex={-1}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center border-b p-4 print:hidden">
              <h2 className="text-xl font-bold text-gray-800">
                Atestado Médico
              </h2>
              <button
                onClick={handleClose}
                className="text-gray-500 hover:text-gray-700 focus:outline-none"
                aria-label="Fechar modal"
              >
                <FaTimes size={20} />
              </button>
            </div>

            <div className="flex flex-1 overflow-hidden">
              {/* Lista de Atestados Existentes */}
              {showAtestadosList && (
                <div className="w-80 border-r bg-gray-50 overflow-y-auto print:hidden">
                  <div className="p-4 border-b">
                    <h3 className="font-bold text-gray-700 mb-2">
                      Atestados Anteriores
                    </h3>
                    <button
                      onClick={() => setShowAtestadosList(false)}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      ← Voltar ao formulário
                    </button>
                  </div>
                  
                  {loadingAtestados ? (
                    <div className="p-4 text-center">
                      <p className="text-gray-500">Carregando atestados...</p>
                    </div>
                  ) : atestadosExistentes.length > 0 ? (
                    <div className="p-2">
                      {atestadosExistentes.map((atestado) => (
                        <div
                          key={atestado.id}
                          className="p-3 mb-2 border rounded-lg bg-white hover:bg-blue-50 cursor-pointer transition-colors"
                          onClick={() => carregarAtestadoExistente(atestado)}
                        >
                          <div className="font-semibold text-sm text-gray-800">
                            {formatDate(atestado.dtConsulta)}
                          </div>
                          <div className="text-xs text-blue-600 mt-1 font-medium">
                            Dr. {atestado.profissional.nmProfissional}
                          </div>
                          <div className="text-xs text-gray-500 truncate mt-1">
                            CID: {atestado.cid || "N/A"} - {atestado.atestadoQtdDias} dias
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-4 text-center">
                      <p className="text-gray-500">Nenhum atestado anterior encontrado</p>
                    </div>
                  )}
                </div>
              )}

              {/* Formulário Principal */}
              <div className={`flex-1 overflow-y-auto flex flex-col ${showAtestadosList ? '' : 'w-full'}`}>
                <div
                  id="printable-area"
                  className="p-6 overflow-y-auto flex-1 text-gray-900 leading-relaxed text-[15px]"
                  style={{ fontFamily: "'Arial', sans-serif" }}
                >
                  <header className="mb-8">
                    <div className="mb-8 text-center">
                      <img
                        src={Logo}
                        alt="Logo Afrafep"
                        className="w-full mb-4 object-contain"
                      />
                      <div className="text-blue-600 font-bold w-full">
                        <div className="text-sm font-normal leading-tight">
                          Associação dos Auditores Fiscais do Estado da Paraíba
                        </div>
                        <div className="text-2xl font-bold leading-tight mt-1">
                          ATESTADO MÉDICO
                        </div>
                      </div>
                    </div>
                  </header>

                  <div className="flex justify-between items-center mb-4 print:hidden">
                    <p>
                      <strong>Paciente:</strong> {professional.nomePaciente}
                    </p>
                    
                    {/* Botão Reutilizar Atestado - só aparece quando NÃO está mostrando a lista */}
                    {!showAtestadosList && atestadosExistentes.length > 0 && (
                      <button
                        onClick={() => setShowAtestadosList(true)}
                        className="bg-green-600 hover:bg-green-700 text-white py-2 px-3 rounded flex items-center gap-2 text-sm"
                      >
                        <FaRecycle />
                        Reutilizar Atestado
                      </button>
                    )}
                  </div>

                  <section className="mb-8">
                    <div className="mb-4">
                      <p className="text-justify">
                        Atesto, para os devidos fins, a pedido do interessado, que o
                        Sr.(a){" "}
                        <input
                          type="text"
                          className={`border-b outline-none text-center ${
                            nomePaciente.trim() === ""
                              ? "border-gray-400"
                              : "focus:border-blue-500"
                          }`}
                          value={nomePaciente}
                          onChange={(e) => setNomePaciente(e.target.value)}
                          style={{ width: "300px" }}
                          placeholder="Digite o nome"
                        />
                        , portador do CPF:{" "}
                        <input
                          type="text"
                          className="border-b border-gray-400 focus:border-blue-500 outline-none text-center"
                          value={cpf}
                          onChange={handleCpfChange}
                          style={{ width: "150px" }}
                        />
                        foi submetido à consulta nesta data, no horário das{" "}
                        <input
                          type="text"
                          className="border-b border-gray-400 focus:border-blue-500 outline-none text-center"
                          value={horarioConsulta}
                          onChange={handleHorarioChange}
                          style={{ width: "80px" }}
                          placeholder="HH:MM"
                          maxLength={5}
                        />
                        , sendo portador de afecção CID-{" "}
                        <input
                          type="text"
                          className="border-b border-red-900 focus:border-blue-500 outline-none text-center"
                          value={cid}
                          onChange={(e) => setCid(e.target.value)}
                          style={{ width: "80px" }}
                        />
                        .
                      </p>
                    </div>

                    <div className="mb-4">
                      <p className="text-justify">
                        Em decorrência deverá permanecer afastado de suas atividades
                        laborativas por um período de{" "}
                        <input
                          type="text"
                          className="border-b border-red-900 focus:border-blue-500 outline-none text-center"
                          value={diasAfastamento}
                          onChange={handleDiasAfastamentoChange} 
                          style={{ width: "50px" }}
                        />{" "}
                        ({" "}
                        <input
                          type="text"
                          className="border-b border-red-900 focus:border-blue-500 outline-none text-center"
                          value={diasEscritoAfastamento}
                          onChange={(e) =>
                            setDiasEscritoAfastamento(e.target.value)
                          }
                          style={{ width: "50px" }}
                          placeholder="auto"
                          readOnly
                        />
                        ) dia(s) a partir desta data. João Pessoa,{" "}
                        <span className="font-semibold">{dataAtestado}</span>.
                      </p>
                    </div>

                    <img
                      src={WatermarkLogo}
                      alt="Marca d'água"
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        width: "300px",
                        height: "auto",
                        opacity: 0.1,
                        transform: "translate(-50%, -50%)",
                        pointerEvents: "none",
                        zIndex: 0,
                      }}
                    />
                  </section>

                  <section className="mt-16 pt-4 border-t text-center">
                    <p className="mb-1">
                      _________________________________________
                    </p>
                    <p className="text-sm font-semibold">
                      Dr. {professional.nmProfissional}
                    </p>
                    <p className="text-sm">CRM: {professional.crm}</p>
                    <p className="text-sm">{professional.especialidade}</p>
                  </section>

                  <section className="mt-8 border-t pt-4">
                    <h3 className="font-bold text-2xl mb-2 text-center">
                      Autorização
                    </h3>
                    <p className="text-justify mb-4">
                      Eu,{" "}
                      <input
                        type="text"
                        className="border-b border-gray-400 focus:border-blue-500 outline-none text-center"
                        value={nomePaciente}
                        onChange={(e) => setNomePaciente(e.target.value)}
                        style={{ width: "300px" }}
                      />
                      , autorizo o Dr. {professional.nmProfissional} a registrar o
                      diagnóstico (codificado - CID:{" "}
                      <input
                        type="text"
                        className="border-b border-red-900 focus:border-blue-500 outline-none text-center"
                        value={cid}
                        onChange={(e) => setCid(e.target.value)}
                        style={{ width: "80px" }}
                      />{" "}
                      ou por extenso) neste atestado.
                    </p>
                    <p className="mt-8 text-center">
                      _________________________________________
                    </p>
                    <p className="text-sm text-center">
                      Assinatura do Usuário - RG
                    </p>
                  </section>

                  <footer className="mt-16 text-xs grid grid-cols-2 gap-8 text-blue-600">
                    <div className="text-left leading-tight">
                      <p>
                        Rua Corálio Soares de Oliveira, 497 • Centro, João Pessoa •
                        PB
                      </p>
                      <p>Fone: (83) 3533.5310 | 3533.5311</p>
                      <p>www.afrafep.org.br</p>
                      <p>INSC. ANS Nº 330281</p>
                    </div>
                    <div className="text-right leading-tight font-light">
                      <p>Ética e Transparência</p>
                      <p>Solidariedade e Responsabilidade Social</p>
                      <p>Comprometimento e Profissionalismo</p>
                      <p>Legalidade e Respeito</p>
                    </div>
                  </footer>
                </div>
              </div>
            </div>

            <div className="border-t p-4 flex justify-end gap-3 print:hidden">
              <button
                onClick={() => setShowAtestadosList(!showAtestadosList)}
                className="bg-gray-900 hover:bg-gray-800 text-white py-2 px-4 rounded flex items-center gap-2"
              >
                <FaRecycle />
                {showAtestadosList ? "Voltar ao Formulário" : "Ver Atestados Anteriores"}
              </button>
              
              <button
                onClick={handleSaveAtestado}
                disabled={isSubmitting}
                className={`bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded flex items-center gap-2 ${
                  isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <FaSave />
                {isSubmitting ? "Enviando..." : "Imprimir Atestado"}
              </button>
            </div>
            <ToastContainer
              position="top-right"
              autoClose={3500}
              hideProgressBar={false}
              toastClassName={() =>
                "bg-black text-white font-semibold rounded shadow-md p-3"
              }
              progressClassName="bg-red-500"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AtestadoMedico;